//  This is an entry point into our application. The bootstrap process and initial render goes here
import * as React from "react";
import * as ReactDOM from "react-dom";
import { EventEmitter } from "fbemitter";
import classnames from "classnames";
import Polyglot from "node-polyglot";
import { Broadcast } from 'react-broadcast';
require('moment/locale/ru');
require('moment/locale/en-gb');

import { Redirect } from "react-router";
import { Router, Route, NavLink } from 'react-router-dom';
import { Header } from './components/Header/Header';
import { EntityLink } from './components/Header/EntityLink';

import { EntityType } from '../models/EntityType';
import { runSearch } from "../rest/Search";
import { signOut } from '../rest/Login';
import { commitEntity } from "../rest/Commit";

import { getUserInfo } from "../rest/user";
import { getGeotabVehicles, getGeotabSensors, getSensorsData, getSensorsDataForIncident } from "../rest/Geotab";
import { getMeasures, getTimezones, getVehicleTags } from "../rest/Misc";
import { getDashboard } from "../rest/Dashboard";
import { takeOverSensorExcess } from "../rest/Dashboard";
import { groupActions } from "../rest/GroupAction";
import { deleteObjects } from "../rest/DeleteObjects";
import { getVehiclesGeoposition } from "../rest/GetVehiclesGeoposition";
import { saveSettings, getSettings } from "../rest/settings";

import { homeRoute, alarmRoute, settingsRoute, appNavigator, routeFor } from "./routes";
import { Container } from './Container';
import { FlagStore } from "./flux/Flag";
import { ErrorStore } from "./flux/Error";
import { ListStore, SelectedItemsStore } from "./flux/Grid";
import { SensorsDataStore } from "./components/Pages/SensorsData/flux";
import { SelectedItemsActions } from "./flux/Grid/SelectedItemsActions";

import { PersonRoute } from "./components/Pages/Person/PersonRoute";
import { ServiceAccountRoute } from "./components/Pages/ServiceAccount/ServiceAccountRoute";
import { VehicleRoute } from "./components/Pages/Vehicle/VehicleRoute";
import { SensorTab } from "./components/Pages/Sensor/SensorRoute";
import { Settings } from "./components/Settings/Settings";
import { SensorsDataRoute } from "./components/Pages/SensorsData/SensorsDataRoute";
import { Button } from "../components/Buttons";

import { TimezoneActions } from "./utils/TimeZone/TimezoneActions"
import { TimezoneStore } from "./utils/TimeZone/TimeZoneStore";
import { VehicleGeopositionStore } from "./components/Pages/Vehicle/flux/VehicleGeopositionStore";
import { AutoImportWatchActions } from "./components/Pages/ServiceAccount/AutoImportWatchActions";
import { VehicleMapRoute } from "./components/Pages/VehicleMap/VehicleMapRoute";
import { GoogleMapLoader } from "./utils/GoogleMapLoader";
import { DivisionPath } from "./components/DivisionPath/DivisionPath";
import { ChangeCurrentDivisionCommand } from "./components/DivisionPath/commands/ChangeCurrentDivisionCommand";
import { CurrentDivisionStore } from "./components/DivisionPath/flux/CurrentDivisionStore";
import { NonIsolatedPremise } from "./components/NonIsolatedPremise";
import { TimeSeriesStore } from "../charts/TimeSeries/flux/TimeSeriesStore";
import { Polyglot as Pl } from "../polyglot";
import { getPrefLocale, languagePacks, getLanguageOptions } from "../polyglot/languagePacks";
import { SignOutCommand } from "../login/commands/SignOutCommand";
import { removeAutCookies, redirectToLogin } from "../login/commands/configure";
import { HomeTab, AlarmTab, DivisionTab, ServiceAccountTab, PersonTab, VehicleTab, SensorTabConfigurator, SettingsTab } from "./init";
import { NotificationCenter } from "./utils/Notifications/NotificationCenter";
import { getNotifications } from "../rest/notifications";
import { getGroupParams } from "../rest/Misc/group-params";
import { SearchFn } from "../rest/Search";

require('./app.global.scss');
const styles = require('./app.scss');

declare var __TIMEZONE_EXPIRE_TIMEOUT_IN_MS__: number;
const timezoneTimeoutMs = __TIMEZONE_EXPIRE_TIMEOUT_IN_MS__;

declare var __AUTO_IMPORT_RELOAD_TIMEOUT_IN_MS__: number;
const autoImportTimeoutMs = __AUTO_IMPORT_RELOAD_TIMEOUT_IN_MS__;

declare var __COMMIT_HASH__: string;
const commitHash = __COMMIT_HASH__;

declare var __NOTIFICATION_PULL_TIMEOUT_MS__: number;
const notificationPullTimeoutMs = __NOTIFICATION_PULL_TIMEOUT_MS__;


const CustomNavLink = (props) => {
  if (props.onClick) {
    return (<a {...props} href="#" onClick={props.onClick}>{props.title}</a>);
  } else {
    return (<NavLink {...props} />);
  }
}


getUserInfo().then(res => {
  const {
    IsAdministrator: isAdmin,
    IsDivisionAware: isDivisionAware,
    IsIsolatedPremise: isIsolatedPremise,
    DefaultTimezoneId: defaultTimezoneId,
    IsEmailNotificationEnabled: isEmailNotificationEnabled,
  } = res;

  let Root;
  const render = () => ReactDOM.render(<Root />, document.getElementById('app'));

  // Create Polyglot and set prefered locale.
  const polyglot: Pl = new Polyglot();
  const locale = (res.Locale && languagePacks.has(res.Locale)) ? res.Locale : getPrefLocale();
  const pack = languagePacks.get(locale);
  polyglot.locale(locale);
  polyglot.replace(pack);

  const SubNav = (props) => {
    let pathParts = window.location.pathname.split("/");
    let currentPath = pathParts[pathParts.length - 1];
    return (
      <ul className="dropdown-menu">
        {
          React.Children.map(props.children, (child, idx) => {
            let child_node = props.children[idx];
            return (<li className={child_node.props && child_node.props.navto && child_node.props.navto.indexOf(currentPath) >= 0 ? 'active' : ''} key={idx}>{child}</li>)
          })
        }
      </ul>
    );
  }

  class DashboardSubNav extends React.Component<any>{

    private readonly selectedItemsActions = new SelectedItemsActions();

    navigateToTimezones() {
      this.selectedItemsActions.selectAll(EntityType.Vehicle);
      this.props.history.push('/vehicle/group-actions');
    }

    render() {
      return (
        <SubNav>
          <CustomNavLink to={homeRoute} exact navto={['dashboard']}>
            {polyglot.t('nav.overview')}
          </CustomNavLink>
          <EntityLink navto={['person']} to='/person' type={EntityType.Person}>{polyglot.t('nav.user_administration')}</EntityLink>
          {isDivisionAware && <EntityLink navto={['division']} to='/division' type={EntityType.Division}>{polyglot.t('nav.fleetAdministration')}</EntityLink>}
          <CustomNavLink navto={['settings']} to={'/settings'}>{polyglot.t('nav.settings_and_configuration')}</CustomNavLink>
          <CustomNavLink navto={['group-actions']} title={polyglot.t('nav.timezones')} to={'/vehicle/group-actions'} onClick={this.navigateToTimezones.bind(this)}>{polyglot.t('nav.timezones')}</CustomNavLink>
          {/* <EntityLink to='/service-account' navto={['service-account']} type={EntityType.ServiceAccount}>{polyglot.t('nav.serviceAccount')}</EntityLink> */}
        </SubNav>
      )
    }
  }

  class AlarmSubNav extends React.Component<any>{

    private readonly selectedItemsActions = new SelectedItemsActions();

    navigateToAlarmsSettings() {
      this.selectedItemsActions.selectAll(EntityType.Vehicle);
      this.props.history.push('/vehicle/group-actions');
    }
    render() {

      return (
        <SubNav>
          <CustomNavLink to={'/alarms'} exact navto={['alarms']}>
            {polyglot.t('nav.overview')}
          </CustomNavLink>
          <CustomNavLink navto={['group-actions']} title={polyglot.t('nav.configure')} to={'/vehicle/group-actions'} onClick={this.navigateToAlarmsSettings.bind(this)}>
            {polyglot.t('nav.configure')}
          </CustomNavLink>
        </SubNav>
      )
    }
  }

  const VehicleSubNav = (props) => {
    return (
      <SubNav>
        <CustomNavLink to={'/vehicle'} exact navto={['vehicle']}>
          {polyglot.t('nav.overview')}
        </CustomNavLink>
        <span></span>
      </SubNav>
    )
  }

  const SensorSubNav = (props) => {
    return (
      <SubNav>
        <CustomNavLink to={'/sensor'} exact navto={['sensor', 'sensors-data']}>
          {polyglot.t('nav.overview')}
        </CustomNavLink>
        <span></span>
      </SubNav>
    )
  }

  const ReportSubNav = (props) => {
    return (
      <SubNav>
        <CustomNavLink to={'/reports'} exact navto={['reports']}>
          {polyglot.t('nav.overview')}
        </CustomNavLink>
        <span></span>
      </SubNav>
    )
  }

  const ReportsMessage = (props) => {
    return (<p>{polyglot.t('reports.no_reports_message')}</p>);
  }

  const googleMapLoader = new GoogleMapLoader();
  const vehicleGeopositionStore = new VehicleGeopositionStore();

  // Current Division
  const divisionEmitter = new EventEmitter();
  const currentDivisionStore = new CurrentDivisionStore();

  // TODO: I don't like it
  const timezoneActions = new TimezoneActions(getTimezones);
  const timezoneStore = new TimezoneStore();

  // Dashboard. Instantiate configuration for Dashboard tab.
  const homeTabConfig = new HomeTab(polyglot, runSearch, getDashboard, takeOverSensorExcess, getSensorsDataForIncident, currentDivisionStore.getCurrentDivisionId, googleMapLoader, deleteObjects);
  const alarmTabConfig = new AlarmTab(polyglot, runSearch, getDashboard, takeOverSensorExcess, getSensorsDataForIncident, currentDivisionStore.getCurrentDivisionId, googleMapLoader, deleteObjects);

  // Current Division
  // Instantiate dependencies for CurrentDivisiton widget
  const changeCurrentDivisionInProgress = new FlagStore();
  const changeCurrentDivisionErrorStore = new ErrorStore();
  const changeCurrentDivisionCmd = new ChangeCurrentDivisionCommand(runSearch, divisionEmitter, changeCurrentDivisionInProgress.dispatchToken, changeCurrentDivisionErrorStore.dispatchToken);

  // Instantiate configuration for each tab into our application
  const divisionTabConfig = new DivisionTab(polyglot, runSearch, commitEntity, currentDivisionStore.getCurrentDivisionId, divisionEmitter, changeCurrentDivisionCmd);
  const personTabConfig = new PersonTab(polyglot, runSearch, commitEntity, deleteObjects, currentDivisionStore.getCurrentDivisionId, divisionEmitter, isAdmin);
  const serviceAccountTabConfig = new ServiceAccountTab(polyglot, runSearch, commitEntity, deleteObjects, currentDivisionStore.getCurrentDivisionId, divisionEmitter, isDivisionAware);
  const vehicleTabConfig = new VehicleTab(polyglot, timezoneStore, defaultTimezoneId, runSearch, commitEntity, deleteObjects, getMeasures, getGroupParams, getVehiclesGeoposition, getVehicleTags, getGeotabVehicles, groupActions, currentDivisionStore.getCurrentDivisionId, divisionEmitter, googleMapLoader);
  vehicleTabConfig.editStore.isIsolatedPremise = isIsolatedPremise;
  const sensorTabConfig = new SensorTabConfigurator(polyglot, runSearch, commitEntity, deleteObjects, getSensorsData, getGeotabSensors, getMeasures, currentDivisionStore.getCurrentDivisionId, divisionEmitter, googleMapLoader);

  // Native brower notifications about Undefined alert levels.
  // We pull server and in case there are undefined alert levels show native browser notification
  const notificationCenter = new NotificationCenter(getNotifications, notificationPullTimeoutMs);
  notificationCenter.addActionHandler('UndefinedAlertLevels', () => vehicleTabConfig.searchUndefinedLevelsCmd.execute({ SummaryCategory: 'UndefinedAlertLevels' }));

  // Here we are attaching listeneter to division change event.
  // When we receive this event we run dependent search in order to update the grid
  divisionEmitter.addListener('division', () => {
    const { pathname } = appNavigator.history.location;
    switch (pathname) {
      case homeRoute:
        vehicleTabConfig.searchConfig.searchBoxProps.refresher.run();
        homeTabConfig.homeLoadDashboardCmd.execute();
        break;

      case alarmRoute:
        vehicleTabConfig.searchConfig.searchBoxProps.refresher.run();
        alarmTabConfig.homeLoadDashboardCmd.execute();
        break;

      case routeFor(EntityType.Division):
        divisionTabConfig.searchConfig.searchBoxProps.refresher.run();
        break;

      case routeFor(EntityType.Person):
        personTabConfig.searchConfig.searchBoxProps.refresher.run();
        break;

      case routeFor(EntityType.ServiceAccount):
        serviceAccountTabConfig.searchConfig.searchBoxProps.refresher.run();
        break;

      case routeFor(EntityType.Vehicle):
        vehicleTabConfig.searchConfig.searchBoxProps.refresher.run();
        break;

      case routeFor(EntityType.Sensor):
        sensorTabConfig.searchConfig.searchBoxProps.refresher.run();
        break;
    }
    
  });


  // Inject dependencies from other tabs
  sensorTabConfig.searchVehicleCmd.labelDispatchToken = vehicleTabConfig.tableTitleStore.dispatchToken;
  sensorTabConfig.searchVehicleCmd.refresher = vehicleTabConfig.searchConfig.searchBoxProps.refresher;

  vehicleTabConfig.searchSensorsCmd.labelDispatchToken = sensorTabConfig.tableTitleStore.dispatchToken;
  vehicleTabConfig.searchSensorsCmd.refresher = sensorTabConfig.searchConfig.searchBoxProps.refresher;

  homeTabConfig.homeSearchVehicleCmd.labelDispatchToken = vehicleTabConfig.tableTitleStore.dispatchToken;
  homeTabConfig.homeSearchVehicleCmd.refresher = vehicleTabConfig.searchConfig.searchBoxProps.refresher;

  homeTabConfig.homeSearchSensorsCmd.labelDispatchToken = sensorTabConfig.tableTitleStore.dispatchToken;
  homeTabConfig.homeSearchSensorsCmd.refresher = sensorTabConfig.searchConfig.searchBoxProps.refresher;

  homeTabConfig.vehicleCategorySearch.labelDispatchToken = vehicleTabConfig.tableTitleStore.dispatchToken;
  homeTabConfig.vehicleCategorySearch.refresher = vehicleTabConfig.searchConfig.searchBoxProps.refresher;

  // Chart
  const sensorsDataStore = new SensorsDataStore();
  const chartStore = new TimeSeriesStore();

  // Settings
  const settingsTabConfig = new SettingsTab(polyglot, languagePacks, getSettings, saveSettings, currentDivisionStore, currentDivisionStore.getCurrentDivision, currentDivisionStore.getCurrentDivisionId, render, isEmailNotificationEnabled, isAdmin);

  // Sign out
  const signOutCmd = new SignOutCommand(signOut, removeAutCookies, redirectToLogin);
  const location = window.location;
  const isDashboard = (location.pathname.indexOf('app/dashboard') > -1);
  
  const currentUser = JSON.parse(localStorage.getItem('currentUser'));
  if(!currentUser || !currentUser.Username){
    signOutCmd.execute();
  }
  // This is jsx markup for our application
  Root = () => (
    <Broadcast channel="nonIsolatedPremise" value={!isIsolatedPremise}>
      <Router history={appNavigator.history}>
        <div className={styles.Container}>
          <Header style={{ display: 'inline-block' }} polyglot={polyglot} history={appNavigator.history}>
            <EntityLink to='/dashboard' subnav={<DashboardSubNav history={appNavigator.history} />} navto={['dashboard', 'person', 'division', 'settings', 'service-account']} type={EntityType.Dashboard}>
              {polyglot.t('nav.dashboard')}
            </EntityLink>
            <EntityLink subnav={<AlarmSubNav history={appNavigator.history} />} to='/alarms' navto={['alarms']} type={EntityType.Alarm}>{polyglot.t('nav.alarms')}</EntityLink>
            <EntityLink to='/vehicle' subnav={<VehicleSubNav history={appNavigator.history} />} navto={['vehicle']} type={EntityType.Vehicle}>{polyglot.t('nav.vehicle')}</EntityLink>
            <EntityLink to='/sensor' subnav={<SensorSubNav history={appNavigator.history} />} navto={['sensor', 'sensors-data']} type={EntityType.Sensor}>{polyglot.t('nav.sensor')}</EntityLink>
            <EntityLink to='/reports' subnav={<ReportSubNav history={appNavigator.history} />} navto={['reports']} type={EntityType.Report}>{polyglot.t('nav.reports')}</EntityLink>
            <a aria-current="true" href="https://help.spartansense.com/spstools/canpimanager" target="_blank">{polyglot.t('nav.canpimanager')}</a>
            <a aria-current="true" href="https://help.spartansense.com/registration" target="_blank">{polyglot.t('nav.registration')}</a>
            <div className={`${styles.Version} hidden-xs`}>version: {commitHash}</div>
          </Header>
          {/*<Route path={'/dashboard'} render={() => <DashboardSubNav history={appNavigator.history} /> } />
          <Route path={'/person'} render={() => <DashboardSubNav history={appNavigator.history} /> } />
          <Route path={'/division'} render={() => <DashboardSubNav history={appNavigator.history} /> } />
          <Route path={'/settings'} render={() => <DashboardSubNav history={appNavigator.history} /> } />
          <Route path={'/service-account'} render={() => <DashboardSubNav history={appNavigator.history} /> } />

          <Route path={'/alarms'} render={() => <AlarmSubNav history={appNavigator.history} /> } />
          <Route path={'/vehicle'} render={() => <VehicleSubNav /> } />
          <Route path={'/vehicles-map'} render={() => <VehicleSubNav /> } />
          <Route path={'/sensor'} render={() => <SensorSubNav /> } />
          <Route path={'/sensors-data'} render={() => <SensorSubNav /> } />
          <Route path={'/reports'} render={() => <ReportSubNav /> } />*/}

          <div className="container-fluid" style={{ width: '100%' }}>
            <Container store={currentDivisionStore}>
              <DivisionPath
                path={null} onChange={d => changeCurrentDivisionCmd.execute(d.id)}
                allDivisions={null} polyglot={polyglot}
              />
            </Container>

            <hr />

            <Route exact path="/" render={() => <Redirect to={homeRoute} />} />
            <Route path={homeRoute} render={() => homeTabConfig.markup} />
            <Route path={alarmRoute} render={() => alarmTabConfig.markup} />
            <Route path={'/reports'} render={() => <ReportsMessage />} />
            <NonIsolatedPremise>
              {serviceAccountTabConfig.markup}
            </NonIsolatedPremise>
            {divisionTabConfig.markup}
            {personTabConfig.markup}
            {vehicleTabConfig.markup}
            {sensorTabConfig.markup}
            <SensorsDataRoute
              polyglot={polyglot}
              store={sensorsDataStore}
              timeSeriesStore={chartStore}
              className={styles.MainRoute} />
            <VehicleMapRoute vehicleGeopositionStore={vehicleGeopositionStore} />
            <Route path={settingsRoute} render={() => settingsTabConfig.markup} />
          </div>
        </div>
      </Router>
    </Broadcast>
  );

  divisionEmitter.once('division', render);

  // Set current DivisionId, which server told us
  // It could be absent when we visit the app for the first time
  const lastDivisionId = res.LastDivisionId;
  changeCurrentDivisionCmd.execute(lastDivisionId);

  timezoneActions.startPullingTimezones(timezoneTimeoutMs);
  notificationCenter.start();

  // Server can be in import process and we have to show import icon.
  // Pull the server in order to update import icon.
  const saAutoImportWatchActions = new AutoImportWatchActions(runSearch, () => serviceAccountTabConfig.listStore.getState().items, EntityType.ServiceAccount);
  saAutoImportWatchActions.startPulling(autoImportTimeoutMs);
  const vehicleAutoImportWatchActions = new AutoImportWatchActions(runSearch, () => vehicleTabConfig.listStore.getState().items, EntityType.Vehicle);
  vehicleAutoImportWatchActions.startPulling(autoImportTimeoutMs);
});


// WEBPACK FOOTER //
// ./src/app/main.tsx